import React, { useState } from "react";
import HeaderMobile from "./HeaderMobile.js";
import Header from "./Header.js";
import Sidebar from "./Sidebar.js";
import Footer from "./Footer.js";
import { BASE_URL } from "./Constants.js";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const PendingIndentReports = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [searchQuery, setSearchQuery] = useState("");
  const [areaFilter, setAreaFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");
  const [colorFilter, setColorFilter] = useState("");
  const [genderFilter, setGenderFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleToggleSidebar = () => {
    if (window.innerWidth >= 992) {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  const fetchReport = async () => {
    if (!fromDate || !toDate) {
      alert("Please select From & To dates");
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("authToken");

    try {
      const res = await fetch(`${BASE_URL}pending_indent_report`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          from_date: fromDate,
          to_date: toDate,
        }),
      });

      const json = await res.json();
      setData(json.status ? json.data : []);
      setCurrentPage(1);
    } catch {
      setData([]);
    }
    setLoading(false);
  };

  // 🔍 Filter Logic
  const filteredData = data.filter((item) => {
    const matchSearch = JSON.stringify(item)
      .toLowerCase()
      .includes(searchQuery.toLowerCase());

    const matchArea = !areaFilter || item.area_name === areaFilter;
    const matchType = !typeFilter || item.dress_type_name === typeFilter;
    const matchSize = !sizeFilter || item.dress_size_name === sizeFilter;
    const matchColor = !colorFilter || item.dress_color_name === colorFilter;
    const matchGender = !genderFilter || item.gender === genderFilter;

    return matchSearch && matchArea && matchType && matchSize && matchColor && matchGender;
  });

  // Pagination
  const indexLast = currentPage * itemsPerPage;
  const currentData = filteredData.slice(indexLast - itemsPerPage, indexLast);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Unique values for filters
  const areaOptions = [...new Set(data.map((i) => i.area_name))];
  const typeOptions = [...new Set(data.map((i) => i.dress_type_name))];
  const sizeOptions = [...new Set(data.map((i) => i.dress_size_name))];
  const colorOptions = [...new Set(data.map((i) => i.dress_color_name))];
  const genderOptions = [...new Set(data.map((i) => i.gender))];

  const handleExcelExport = () => {
    const exportData = filteredData.map((item) => ({
      ID: item.id,
      Date: item.created_on?.substring(0, 10),
      Name: item.candidate_name,
      Mobile: item.mobile,
      Gender: item.gender,
      Department: item.department,
      Location: item.location,
      Area: item.area_name,
      Type: item.dress_type_name,
      Size: item.dress_size_name,
      Color: item.dress_color_name,
      Qty: item.uniform_qty,
      Cost: item.cost,
      PaymentStatus: item.payment_status,
      DeliveryStatus: item.delivery_status,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Pending Indents");

    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })]),
      "Pending_Indents_Report.xlsx"
    );
  };

  return (
    <div className="page-wrapper">
      <HeaderMobile />
      <Sidebar isOpen={isSidebarOpen} />

      <div
        className="page-container"
        style={{ marginLeft: isSidebarOpen ? "250px" : "0" }}
      >
        <Header onToggleSidebar={handleToggleSidebar} isSidebarOpen={isSidebarOpen} />
        <div className="main-content">
          <div className="section__content section__content--p30">
            <h4 className="mb-3">Pending Indent Report</h4>

            {/* Filters */}
            <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">

              <div>
                <label><b>From</b></label>
                <input type="date" className="form-control"
                  value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              </div>

              <div>
                <label><b>To</b></label>
                <input type="date" className="form-control"
                  value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>

              {data.length > 0 && (
                <>
                  <div>
                    <label><b>Search</b></label>
                    <input type="text" className="form-control"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search here"
                    />
                  </div>

                  <div>
                    <label><b>Area</b></label>
                    <select className="form-control"
                      value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}>
                      <option value="">All</option>
                      {areaOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label><b>Type</b></label>
                    <select className="form-control"
                      value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                      <option value="">All</option>
                      {typeOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label><b>Size</b></label>
                    <select className="form-control"
                      value={sizeFilter} onChange={(e) => setSizeFilter(e.target.value)}>
                      <option value="">All</option>
                      {sizeOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label><b>Color</b></label>
                    <select className="form-control"
                      value={colorFilter} onChange={(e) => setColorFilter(e.target.value)}>
                      <option value="">All</option>
                      {colorOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label><b>Gender</b></label>
                    <select className="form-control"
                      value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)}>
                      <option value="">All</option>
                      {genderOptions.map((a) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <button className="btn btn-primary" onClick={fetchReport}>
                Search
              </button>

              {data.length > 0 && (
                <button className="btn btn-success" onClick={handleExcelExport}>
                  Excel
                </button>
              )}

            </div>

            {/* Table */}
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : filteredData.length === 0 ? (
              <div className="text-danger">No Data Found</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Mobile</th>
                      <th>Gender</th>
                      <th>Department</th>
                      <th>Location</th>
                      <th>Area</th>
                      <th>Type</th>
                      <th>Size</th>
                      <th>Color</th>
                      <th>Qty</th>
                      <th>Cost</th>
                      <th>Payment</th>
                      <th>Delivery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.created_on?.substring(0, 10)}</td>
                        <td>{item.candidate_name}</td>
                        <td>{item.mobile}</td>
                        <td>{item.gender}</td>
                        <td>{item.department}</td>
                        <td>{item.location}</td>
                        <td>{item.area_name}</td>
                        <td>{item.dress_type_name}</td>
                        <td>{item.dress_size_name}</td>
                        <td>{item.dress_color_name}</td>
                        <td>{item.uniform_qty}</td>
                        <td>{item.cost}</td>
                        <td>{item.payment_status}</td>
                        <td>{item.delivery_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-center my-3">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`btn btn-sm mx-1 ${currentPage === i + 1 ? "btn-primary" : "btn-light"}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PendingIndentReports;
